import { useCallback, useMemo, useState } from "react";
import { ImportedAccount } from "@common/schemas/domains/accounting/importedAccount.schema";
import { Account } from "@common/types/domains/accounting/account";
import { resolveMappedKickAccount } from "../resolveMappedKickAccount";
import { resolveCreateAccountFromRowInitialValues } from "./createAccountFromRow";

interface CreateModalTarget {
    importedAccountId: string;
    name?: string;
}

interface UseMappingRowCreationParams {
    importedAccounts: ImportedAccount[] | undefined;
    kickAccountsById: Record<string, Account | undefined>;
}

export function useMappingRowCreation({
    importedAccounts,
    kickAccountsById,
}: UseMappingRowCreationParams) {
    const [createModalTarget, setCreateModalTarget] =
        useState<CreateModalTarget | null>(null);
    const createModal = useMemo(() => {
        if (createModalTarget === null) {
            return undefined;
        }
        const account = importedAccounts?.find(
            (candidate) => candidate.id === createModalTarget.importedAccountId,
        );
        if (!account) {
            return undefined;
        }
        return {
            account,
            initialValues: resolveCreateAccountFromRowInitialValues({
                account,
                mappedKickAccount: resolveMappedKickAccount({
                    account,
                    kickAccountsById,
                }),
                typedName: createModalTarget.name,
            }),
        };
    }, [importedAccounts, kickAccountsById, createModalTarget]);
    const closeCreateModal = useCallback(() => setCreateModalTarget(null), []);
    const onOpenCreateAccountModal = useCallback(
        (target: CreateModalTarget) => setCreateModalTarget(target),
        [],
    );

    return {
        createModal,
        closeCreateModal,
        onOpenCreateAccountModal,
    };
}
